<script setup>
import { useAlert } from 'dashboard/composables';
import AddScheduledMessage from './AddScheduledMessage.vue';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import { computed, onMounted, ref, defineOptions } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters, useStore } from 'dashboard/composables/store';

import Button from 'dashboard/components-next/button/Button.vue';
import {
    BaseTable,
    BaseTableRow,
    BaseTableCell,
} from 'dashboard/components-next/table';

defineOptions({
    name: 'ScheduledMessagesSettings',
});

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();

const showAddPopup = ref(false);
const showEditPopup = ref(false);
const showDeleteConfirmationPopup = ref(false);
const activeMessage = ref({});
const loading = ref({});

const searchQuery = ref('');
const activeTab = ref('all');
const currentPage = ref(1);
const perPage = ref(15);

const records = computed(() => getters.getScheduledMessages.value);

const filteredByTab = computed(() => {
    const all = records.value;
    if (activeTab.value === 'all') return all;
    return all.filter(r => r.status === activeTab.value);
});

const filteredRecords = computed(() => {
    const query = searchQuery.value.trim().toLowerCase();
    if (!query) return filteredByTab.value;
    return filteredByTab.value.filter(
        r =>
            (r.content && r.content.toLowerCase().includes(query)) ||
            (r.conversation &&
                r.conversation.contact_name &&
                r.conversation.contact_name.toLowerCase().includes(query)) ||
            String(r.conversation_id).includes(query)
    );
});

const totalRecords = computed(() => filteredRecords.value.length);
const totalPages = computed(() =>
    Math.max(1, Math.ceil(totalRecords.value / perPage.value))
);

const paginatedRecords = computed(() => {
    const start = (currentPage.value - 1) * perPage.value;
    return filteredRecords.value.slice(start, start + perPage.value);
});

const uiFlags = computed(() => getters.getScheduledMessagesUIFlags.value);

const deleteMessage = computed(() => {
    return ` #${activeMessage.value.id} ? `;
});

const fetchScheduledMessages = async () => {
    try {
        await store.dispatch('getScheduledMessages');
    } catch {
        // Ignore
    }
};

onMounted(() => {
    fetchScheduledMessages();
});

const showAlertMessage = message => {
    loading.value[activeMessage.value.id] = false;
    activeMessage.value = {};
    useAlert(message);
};

const openAddPopup = () => {
    showAddPopup.value = true;
};
const hideAddPopup = () => {
    showAddPopup.value = false;
};
const openEditPopup = msg => {
    activeMessage.value = msg;
    showEditPopup.value = true;
};
const hideEditPopup = () => {
    showEditPopup.value = false;
    activeMessage.value = {};
};

const openDeletePopup = msg => {
    showDeleteConfirmationPopup.value = true;
    activeMessage.value = msg;
};

const closeDeletePopup = () => {
    showDeleteConfirmationPopup.value = false;
};

const deleteScheduledMessage = async id => {
    try {
        await store.dispatch('deleteScheduledMessage', id);
        showAlertMessage(t('SCHEDULED_MSGS.DELETE.API.SUCCESS_MESSAGE'));
    } catch (error) {
        const errorMessage =
            error?.message || t('SCHEDULED_MSGS.DELETE.API.ERROR_MESSAGE');
        showAlertMessage(errorMessage);
    }
};

const confirmDeletion = () => {
    loading.value[activeMessage.value.id] = true;
    closeDeletePopup();
    deleteScheduledMessage(activeMessage.value.id);
};

const tableHeaders = computed(() => [
    t('SCHEDULED_MSGS.LIST.TABLE_HEADER.CONTENT'),
    t('SCHEDULED_MSGS.LIST.TABLE_HEADER.ACTIONS'),
]);

const tabs = computed(() => [
    { key: 'all', label: t('SCHEDULED_MSGS.TABS.ALL') },
    { key: 'pending', label: t('SCHEDULED_MSGS.TABS.PENDING') },
    { key: 'sent', label: t('SCHEDULED_MSGS.TABS.SENT') },
    { key: 'failed', label: t('SCHEDULED_MSGS.TABS.FAILED') },
    { key: 'cancelled', label: t('SCHEDULED_MSGS.TABS.CANCELLED') },
]);

const onTabChange = key => {
    activeTab.value = key;
    currentPage.value = 1;
};

const onSearchChange = () => {
    currentPage.value = 1;
};

const goToPage = page => {
    if (page >= 1 && page <= totalPages.value) {
        currentPage.value = page;
    }
};

const statusColor = status => {
    const colors = {
        pending: 'bg-n-amber-2 text-n-amber-11',
        sent: 'bg-n-teal-2 text-n-teal-11',
        failed: 'bg-n-ruby-2 text-n-ruby-11',
        cancelled: 'bg-n-slate-3 text-n-slate-11',
    };
    return colors[status] || 'bg-n-slate-3 text-n-slate-11';
};

const formatDate = date => {
    if (!date) return '—';
    return new Date(date).toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const truncate = (text, len = 80) => {
    if (!text) return '—';
    return text.length > len ? text.substring(0, len) + '...' : text;
};

const visiblePages = computed(() => {
    const pages = [];
    const total = totalPages.value;
    const current = currentPage.value;
    const maxVisible = 5;

    if (total <= maxVisible) {
        for (let i = 1; i <= total; i++) pages.push(i);
    } else {
        pages.push(1);
        let start = Math.max(2, current - 1);
        let end = Math.min(total - 1, current + 1);
        if (current <= 3) end = Math.min(4, total - 1);
        if (current >= total - 2) start = Math.max(total - 3, 2);
        if (start > 2) pages.push('...');
        for (let i = start; i <= end; i++) pages.push(i);
        if (end < total - 1) pages.push('...');
        pages.push(total);
    }
    return pages;
});
</script>

<template>
    <SettingsLayout :is-loading="uiFlags.fetchingList" :loading-message="$t('SCHEDULED_MSGS.LOADING')"
        :no-records-found="!records.length" :no-records-message="$t('SCHEDULED_MSGS.LIST.404')">
        <template #header>
            <BaseSettingsHeader v-model:search-query="searchQuery" :title="$t('SCHEDULED_MSGS.HEADER')"
                :description="$t('SCHEDULED_MSGS.DESCRIPTION')"
                :search-placeholder="$t('SCHEDULED_MSGS.SEARCH_PLACEHOLDER')" feature-name="scheduled_messages"
                @update:search-query="onSearchChange">
                <template #actions>
                    <Button :label="$t('SCHEDULED_MSGS.HEADER_BTN_TXT')" size="sm" @click="openAddPopup" />
                </template>
            </BaseSettingsHeader>
        </template>

        <template #body>
            <div class="flex gap-1 mb-4 border-b border-n-weak">
                <button v-for="tab in tabs" :key="tab.key"
                    class="px-4 py-2 text-sm font-medium transition-colors cursor-pointer rounded-t-lg" :class="{
                        'text-n-brand bg-n-alpha-black2 border-b-2 border-n-brand':
                            activeTab === tab.key,
                        'text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-black1':
                            activeTab !== tab.key,
                    }" @click="onTabChange(tab.key)">
                    {{ tab.label }}
                    <span v-if="tab.key === 'all'" class="ml-1 text-xs text-n-slate-10">
                        ({{ records.length }})
                    </span>
                </button>
            </div>

            <BaseTable :headers="tableHeaders" :items="paginatedRecords" :no-data-message="!records.length
                ? $t('SCHEDULED_MSGS.LIST.404')
                : searchQuery
                    ? $t('SCHEDULED_MSGS.NO_RESULTS')
                    : ''
                ">
                <template #header-0>
                    {{ tableHeaders[0] }}
                </template>
                <template #header-1>
                    {{ tableHeaders[1] }}
                </template>

                <template #row="{ items }">
                    <BaseTableRow v-for="msg in items" :key="msg.id" :item="msg">
                        <template #default>
                            <BaseTableCell class="max-w-0">
                                <div class="flex flex-col gap-2 min-w-0">
                                    <div class="flex items-center gap-2">
                                        <span class="text-heading-3 text-n-slate-12">
                                            #{{ msg.conversation?.display_id || msg.conversation_id }}
                                        </span>
                                        <span v-if="msg.conversation?.contact_name" class="text-xs text-n-slate-11">
                                            · {{ msg.conversation.contact_name }}
                                        </span>
                                        <span
                                            class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0"
                                            :class="statusColor(msg.status)">
                                            {{ $t(`SCHEDULED_MSGS.STATUS.${msg.status.toUpperCase()}`) }}
                                        </span>
                                    </div>
                                    <p class="text-body-main text-n-slate-11 truncate" :title="msg.content">
                                        {{ truncate(msg.content) }}
                                    </p>
                                    <div class="flex items-center gap-3 text-xs text-n-slate-10">
                                        <span>🕐 {{ formatDate(msg.scheduled_at) }}</span>
                                        <span v-if="msg.sent_at" class="text-n-teal-11">
                                            ✅ {{ formatDate(msg.sent_at) }}
                                        </span>
                                    </div>
                                    <p v-if="msg.error_message" class="text-xs text-n-ruby-11">
                                        ⚠️ {{ msg.error_message }}
                                    </p>
                                </div>
                            </BaseTableCell>

                            <BaseTableCell align="end" class="w-24">
                                <div class="flex gap-3 justify-end flex-shrink-0">
                                    <Button v-if="msg.status === 'pending'" v-tooltip.top="'Editar'"
                                        icon="i-lucide-pencil" slate sm @click="openEditPopup(msg)" />
                                    <Button v-if="msg.status === 'pending'"
                                        v-tooltip.top="$t('SCHEDULED_MSGS.DELETE.BUTTON_TEXT')" icon="i-woot-bin" slate
                                        sm class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
                                        :is-loading="loading[msg.id]" @click="openDeletePopup(msg)" />
                                </div>
                            </BaseTableCell>
                        </template>
                    </BaseTableRow>
                </template>
            </BaseTable>

            <div v-if="totalRecords > perPage" class="flex items-center justify-between mt-4 px-2">
                <p class="text-sm text-n-slate-11">
                    {{ (currentPage - 1) * perPage + 1 }}–{{
                        Math.min(currentPage * perPage, totalRecords)
                    }}
                    {{ $t('SCHEDULED_MSGS.PAGINATION.OF') }} {{ totalRecords }}
                </p>

                <nav class="flex items-center gap-1">
                    <button
                        class="p-1.5 rounded-lg text-sm text-n-slate-11 hover:bg-n-alpha-black2 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                        :disabled="currentPage === 1" @click="goToPage(currentPage - 1)">
                        ‹
                    </button>

                    <template v-for="page in visiblePages" :key="page">
                        <span v-if="page === '...'" class="px-2 text-sm text-n-slate-10">
                            …
                        </span>
                        <button v-else
                            class="min-w-[2rem] h-8 rounded-lg text-sm font-medium transition-colors cursor-pointer"
                            :class="{
                                'bg-n-brand text-white': page === currentPage,
                                'text-n-slate-11 hover:bg-n-alpha-black2':
                                    page !== currentPage,
                            }" @click="goToPage(page)">
                            {{ page }}
                        </button>
                    </template>

                    <button
                        class="p-1.5 rounded-lg text-sm text-n-slate-11 hover:bg-n-alpha-black2 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                        :disabled="currentPage === totalPages" @click="goToPage(currentPage + 1)">
                        ›
                    </button>
                </nav>
            </div>
        </template>

        <woot-modal v-model:show="showAddPopup" :on-close="hideAddPopup">
            <AddScheduledMessage :on-close="hideAddPopup" />
        </woot-modal>

        <woot-modal v-model:show="showEditPopup" :on-close="hideEditPopup">
            <AddScheduledMessage :on-close="hideEditPopup" :edit-mode="true" :initial-data="activeMessage" />
        </woot-modal>

        <woot-delete-modal v-model:show="showDeleteConfirmationPopup" :on-close="closeDeletePopup"
            :on-confirm="confirmDeletion" :title="$t('SCHEDULED_MSGS.DELETE.CONFIRM.TITLE')"
            :message="$t('SCHEDULED_MSGS.DELETE.CONFIRM.MESSAGE')" :message-value="deleteMessage"
            :confirm-text="`${$t('SCHEDULED_MSGS.DELETE.CONFIRM.YES')} #${activeMessage.id}`"
            :reject-text="`${$t('SCHEDULED_MSGS.DELETE.CONFIRM.NO')} #${activeMessage.id}`" />
    </SettingsLayout>
</template>
